import { Component } from '@theme/component';

/**
 * Makes a horizontal scroller draggable with a pointer.
 *
 * The row is a native `overflow-x` scroller, so touch, wheel, keyboard and scrollbar already work
 * — a mouse is the only input that cannot swipe one. This adds exactly that and nothing else: no
 * scroll position is owned here, `scrollLeft` stays the single source of truth.
 *
 * Wrap the scroller in `<drag-scroller>` and mark the scrolling element `ref="row"`. The component
 * sets two attributes on that element and styles nothing itself, so each section keeps its own
 * cursor and snap rules:
 *
 *   - `data-draggable`  the row currently overflows, so there is somewhere to drag to
 *   - `data-dragging`   a drag is in progress (turn `scroll-snap-type` off and `user-select` off)
 *
 * Consumers: `sections/bundle.liquid` (Build Your Own Itinerary),
 * `sections/footer-photo-strip.liquid` (static mode only — in scrolling mode the row is a marquee
 * and a drag would fight it).
 *
 * See docs/build-your-own-itinerary.md.
 *
 * @extends Component<{ row: HTMLElement }>
 */
class DragScroller extends Component {
  requiredRefs = ['row'];

  /** Pointer id of the drag in progress, or null. @type {number | null} */
  #pointer = null;

  /** Where the drag started, and the scroll offset it started from. */
  #startX = 0;
  #startScroll = 0;

  /** Travelled far enough to count as a drag rather than a click. */
  #moved = false;

  /**
   * Swallow the one click that closes a drag — and only that one. It must not be the same flag as
   * `#moved`: a `pointercancel` (alt-tab, OS gesture, pointer leaving the window) ends a drag with
   * no click behind it, so a flag cleared only by a click would stay set and then eat the next
   * unrelated activation — including a keyboard Enter or a touch tap, neither of which goes through
   * `#onDown`. Cleared on a timeout as well, which always runs after the synthetic click.
   */
  #suppressClick = false;

  /** Recent samples, for the throw. @type {Array<{ x: number, t: number }>} */
  #samples = [];

  /** @type {number | null} */
  #momentum = null;

  /** Past this many pixels the gesture is a drag and the click that ends it is suppressed. */
  static DRAG_THRESHOLD = 4;

  connectedCallback() {
    super.connectedCallback();

    const { row } = this.refs;
    const { signal } = (this.dragController = new AbortController());

    // Coarse pointers already swipe natively; hooking them would fight the browser's own inertia.
    row.addEventListener('pointerdown', this.#onDown, { signal });
    row.addEventListener('pointermove', this.#onMove, { signal });
    row.addEventListener('pointerup', this.#onUp, { signal });
    row.addEventListener('pointercancel', this.#onUp, { signal });
    // Capture, so a drag that ends on a card's link is swallowed before the link sees it.
    row.addEventListener('click', this.#onClick, { capture: true, signal });
    // Images (and links) are natively draggable: without this, pressing the mouse on a photograph
    // and moving starts the browser's own drag-and-drop — the ghost image rides the cursor and the
    // pointer stream ends in `pointercancel`, killing the scroll drag it was meant to be.
    row.addEventListener('dragstart', this.#onDragStart, { signal });

    this.#syncAffordance();
    this.#observer = new ResizeObserver(() => this.#syncAffordance());
    this.#observer.observe(row);
    // A ResizeObserver on the row never fires when a card is added or removed — the row keeps its
    // width and only its content changes. That is exactly what happens in the theme editor.
    this.#cardObserver = new MutationObserver(() => this.#syncAffordance());
    this.#cardObserver.observe(row, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.dragController?.abort();
    this.#observer?.disconnect();
    this.#cardObserver?.disconnect();
    this.#stopMomentum();
  }

  /** @type {ResizeObserver | null} */
  #observer = null;

  /** @type {MutationObserver | null} */
  #cardObserver = null;

  /** Only advertise the grab cursor while there is somewhere to drag to. */
  #syncAffordance() {
    const { row } = this.refs;
    const overflows = row.scrollWidth > row.clientWidth + 1;
    row.toggleAttribute('data-draggable', overflows);
    if (!overflows) row.removeAttribute('data-dragging');
  }

  /** @param {PointerEvent} event */
  #onDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    // A drag is already being tracked; a second press cannot start another.
    if (this.#pointer !== null) return;

    const { row } = this.refs;
    if (row.scrollWidth <= row.clientWidth + 1) return;

    this.#stopMomentum();
    this.#pointer = event.pointerId;
    this.#startX = event.clientX;
    this.#startScroll = row.scrollLeft;
    this.#moved = false;
    this.#samples = [{ x: event.clientX, t: event.timeStamp }];
  };

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    if (this.#pointer !== event.pointerId) return;

    const { row } = this.refs;
    const dx = event.clientX - this.#startX;

    if (!this.#moved) {
      if (Math.abs(dx) < DragScroller.DRAG_THRESHOLD) return;
      this.#moved = true;
      row.setAttribute('data-dragging', '');
      // Only captured once it IS a drag, so a plain click on a card still reaches the card.
      row.setPointerCapture(event.pointerId);
    }

    row.scrollLeft = this.#startScroll - dx;

    this.#samples.push({ x: event.clientX, t: event.timeStamp });
    if (this.#samples.length > 5) this.#samples.shift();
  };

  /** @param {PointerEvent} event */
  #onUp = (event) => {
    if (this.#pointer !== event.pointerId) return;

    const { row } = this.refs;
    if (row.hasPointerCapture?.(event.pointerId)) row.releasePointerCapture(event.pointerId);
    this.#pointer = null;

    const wasDrag = this.#moved;
    this.#moved = false;
    if (!wasDrag) return;

    row.removeAttribute('data-dragging');
    this.#suppressClick = true;
    window.setTimeout(() => {
      this.#suppressClick = false;
    }, 0);

    // A cancelled pointer's final coordinates are not guaranteed meaningful, so it settles rather
    // than throwing.
    if (event.type === 'pointercancel') this.#settle();
    else this.#throw(event);
  };

  /**
   * Click that closes a drag never counts as a click — otherwise dragging from a card's button
   * fires it on release.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!this.#suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.#suppressClick = false;
  };

  /**
   * Suppresses the native image/link drag only while a mouse drag is being tracked — `#pointer`
   * is only ever set for a primary-button mouse press, so touch and every other interaction keep
   * the browser's default behavior.
   * @param {DragEvent} event
   */
  #onDragStart = (event) => {
    if (this.#pointer === null) return;
    event.preventDefault();
  };

  /**
   * Carries the release velocity for a moment, then hands the row back to CSS snapping.
   * @param {PointerEvent} event
   */
  #throw(event) {
    const first = this.#samples[0];
    const elapsed = event.timeStamp - (first?.t ?? event.timeStamp);
    if (!first || elapsed <= 0) return this.#settle();

    let velocity = (event.clientX - first.x) / elapsed;
    if (Math.abs(velocity) < 0.05 || this.#prefersReducedMotion) return this.#settle();

    const { row } = this.refs;
    let last = event.timeStamp;

    const step = (now) => {
      const dt = now - last;
      last = now;
      row.scrollLeft -= velocity * dt;
      // ~halves every 100ms, framerate-independent.
      velocity *= Math.pow(0.995, dt);

      if (Math.abs(velocity) > 0.02) {
        this.#momentum = requestAnimationFrame(step);
      } else {
        this.#momentum = null;
        this.#settle();
      }
    };

    this.#momentum = requestAnimationFrame(step);
  }

  /** Re-enables snapping once the row is still, so it lands on a card edge. */
  #settle() {
    this.refs.row.removeAttribute('data-dragging');
  }

  #stopMomentum() {
    if (this.#momentum === null) return;
    cancelAnimationFrame(this.#momentum);
    this.#momentum = null;
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('drag-scroller')) customElements.define('drag-scroller', DragScroller);
